import { z } from 'zod';
import { isoTimestamp } from './common';
import { resourceNameSchema, valueRefSchema } from './tools';

/** Languages a sandbox runs; the code is written to `main.<ext>` and run by the interpreter. */
export const sandboxRuntimeSchema = z.enum(['python', 'node', 'bash']);
export type SandboxRuntime = z.infer<typeof sandboxRuntimeSchema>;
export const SANDBOX_FILE_EXTENSIONS: Record<SandboxRuntime, string> = {
  python: 'py',
  node: 'mjs',
  bash: 'sh',
};

/**
 * - `local`: an interpreter on the API host from the operator's `SANDBOX_LOCAL_ALLOWLIST`, run in a
 *   scratch directory with a minimal environment, a time limit and output caps, isolated with
 *   Bubblewrap when `SANDBOX_ISOLATION=bwrap` (the production default; fails closed if unavailable);
 * - `remote`: an HTTP code-execution service (your own runner, or a hosted sandbox behind a small
 *   adapter) that receives `{ runtime, code, timeoutMs }` and returns `{ stdout, stderr, exitCode }`.
 */
export const sandboxProviderSchema = z.enum(['local', 'remote']);
export type SandboxProvider = z.infer<typeof sandboxProviderSchema>;

/** What this server allows for local sandboxes (`GET /sandboxes/capabilities`). */
export const sandboxCapabilitiesSchema = z.object({
  localAllowlist: z.array(z.string()),
  isolation: z.enum(['bwrap', 'none']),
});
export type SandboxCapabilities = z.infer<typeof sandboxCapabilitiesSchema>;

const headerName = z.string().regex(/^[A-Za-z0-9-]{1,100}$/, 'Invalid header name');
const common = {
  name: resourceNameSchema,
  description: z.string().trim().max(2000).default(''),
  runtime: sandboxRuntimeSchema,
  timeoutMs: z.number().int().min(100).max(120_000).default(30_000),
  outputMaxBytes: z.number().int().min(1024).max(1_048_576).default(65_536),
};

export const createSandboxRequestSchema = z.discriminatedUnion('provider', [
  z.object({
    ...common,
    provider: z.literal('local'),
    command: z
      .string()
      .regex(/^\/[^\0]*$/, 'Use an absolute path')
      .max(1024),
  }),
  z.object({
    ...common,
    provider: z.literal('remote'),
    url: z.url({ protocol: /^https?$/ }).max(2048),
    headers: z.record(headerName, valueRefSchema).default({}),
  }),
]);
export type CreateSandboxRequest = z.infer<typeof createSandboxRequestSchema>;

export const updateSandboxRequestSchema = z
  .object({
    description: common.description.optional(),
    timeoutMs: common.timeoutMs.optional(),
    outputMaxBytes: common.outputMaxBytes.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export type UpdateSandboxRequest = z.infer<typeof updateSandboxRequestSchema>;

export const sandboxDtoSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string(),
  provider: sandboxProviderSchema,
  runtime: sandboxRuntimeSchema,
  command: z.string().nullable(),
  url: z.string().nullable(),
  headers: z.record(z.string(), valueRefSchema),
  timeoutMs: z.number().int(),
  outputMaxBytes: z.number().int(),
  createdAt: isoTimestamp,
  updatedAt: isoTimestamp,
});
export type SandboxDto = z.infer<typeof sandboxDtoSchema>;

export const executeSandboxRequestSchema = z.object({
  code: z.string().min(1, 'Write some code first').max(100_000),
});
export type ExecuteSandboxRequest = z.infer<typeof executeSandboxRequestSchema>;

export const sandboxExecutionStatusSchema = z.enum(['succeeded', 'failed', 'timeout', 'error']);
export type SandboxExecutionStatus = z.infer<typeof sandboxExecutionStatusSchema>;
export const sandboxExecutionSourceSchema = z.enum(['console', 'agent', 'workflow']);
export type SandboxExecutionSource = z.infer<typeof sandboxExecutionSourceSchema>;

export const sandboxExecutionDtoSchema = z.object({
  id: z.uuid(),
  sandboxId: z.uuid(),
  source: sandboxExecutionSourceSchema,
  status: sandboxExecutionStatusSchema,
  code: z.string(),
  stdout: z.string(),
  stderr: z.string(),
  exitCode: z.number().int().nullable(),
  /** Why the sandbox could not run the code (`error` status). */
  message: z.string().nullable(),
  durationMs: z.number().int(),
  runId: z.uuid().nullable(),
  createdAt: isoTimestamp,
});
export type SandboxExecutionDto = z.infer<typeof sandboxExecutionDtoSchema>;

/** Creates an agent tool (`execute_code`-style) backed by the sandbox. */
export const sandboxToolRequestSchema = z.object({
  name: z
    .string()
    .regex(
      /^[a-z][a-z0-9_]{0,63}$/,
      'Use 1–64 lowercase letters, digits or _, starting with a letter',
    ),
  description: z.string().trim().min(1).max(2000),
});
export type SandboxToolRequest = z.infer<typeof sandboxToolRequestSchema>;

/** The fixed input schema of a sandbox tool. */
export const SANDBOX_TOOL_INPUT_SCHEMA = {
  type: 'object',
  properties: {
    code: { type: 'string', description: 'The complete program to run. Print results to stdout.' },
  },
  required: ['code'],
  additionalProperties: false,
} as const;
